'use client';

import clsx from 'clsx';
import { H3, P, Span } from '@/components/ui';
import { widgetSurfaceClass } from '@/components/widget/_lib/widgetStandard';
import { useEffect, useState } from 'react';

export type WindowAccordionSlide = {
  number?: string;
  brand: string;
  name: string;
  subtitle: string;
  specs?: { label: string; value: string }[];
  badges?: { label: string }[];
};

const MOBILE_MQ = '(max-width: 1024px)';

export type WindowAccordionSliderProps = {
  slides: readonly WindowAccordionSlide[];
  controlledActiveIndex?: number;
  className?: string;
};

export default function WindowAccordionSlider({
  slides,
  controlledActiveIndex,
  className,
}: WindowAccordionSliderProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpenIndex, setMobileOpenIndex] = useState(-1);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (typeof controlledActiveIndex === 'number') {
      setActiveIndex(controlledActiveIndex);
    }
  }, [controlledActiveIndex]);

  if (!slides.length) return null;

  const desktopActive =
    typeof controlledActiveIndex === 'number' ? controlledActiveIndex : activeIndex;

  return (
    <div
      className={widgetSurfaceClass(
        'window-accordion-slider',
        clsx('widget-window-accordion', className)
      )}
      data-widget="WindowAccordionSlider"
    >
      <div
        className={clsx(
          'widget-window-accordion__track',
          isMobile && 'widget-window-accordion__track--mobile'
        )}
      >
        {slides.map((slide, index) => {
          const isDesktopActive = !isMobile && index === desktopActive;
          const isMobileOpen = isMobile && mobileOpenIndex === index;
          const isActive = isMobile ? isMobileOpen : isDesktopActive;
          const isCollapsed = !isActive;
          const stepLabel = slide.number ?? String(index + 1).padStart(2, '0');
          const collapsedTitle = `${stepLabel} – ${slide.name}`;

          return (
            <div
              key={`${slide.name}-${index}`}
              role="group"
              aria-expanded={isActive}
              aria-label={collapsedTitle}
              onMouseEnter={() => {
                if (!isMobile && controlledActiveIndex === undefined) {
                  setActiveIndex(index);
                }
              }}
              onMouseLeave={() => {
                if (!isMobile && controlledActiveIndex === undefined) {
                  setActiveIndex(0);
                }
              }}
              className={clsx(
                'page-glass-panel page-glass-panel--elevated widget-window-accordion__panel p-0',
                isCollapsed && !isMobile && 'widget-window-accordion__panel--collapsed',
                isActive && 'overflow-hidden',
                !isActive && isMobile && 'overflow-hidden',
                !isMobile && 'widget-window-accordion__panel--desktop',
                isActive && 'widget-window-accordion__panel--active'
              )}
            >
              <div
                className={clsx(
                  'widget-window-accordion__header',
                  isCollapsed && 'widget-window-accordion__header--collapsed',
                  isCollapsed && isMobile && 'widget-window-accordion__header--collapsed-mobile'
                )}
              >
                {isCollapsed ? (
                  <>
                    <Span
                      className="widget-window-accordion__collapsed-label"
                      title={collapsedTitle}
                    >
                      <span className="widget-window-accordion__collapsed-graffiti">
                        <span className="widget-window-accordion__collapsed-num">{stepLabel}</span>
                        <span className="widget-window-accordion__collapsed-sep" aria-hidden>
                          {' '}
                          –{' '}
                        </span>
                        <span className="widget-window-accordion__collapsed-name">{slide.name}</span>
                      </span>
                    </Span>
                    {isMobile ? (
                      <button
                        type="button"
                        className="faq-accordion-trigger ml-auto shrink-0 px-1 py-0.5"
                        aria-expanded={isMobileOpen}
                        aria-label={isMobileOpen ? 'Collapse' : 'Expand'}
                        onClick={() => {
                          setMobileOpenIndex((prev) => (prev === index ? -1 : index));
                        }}
                      >
                        <Span className="text-lg font-medium text-prose-muted" aria-hidden>
                          +
                        </Span>
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="widget-window-accordion__header-copy min-w-0 flex-1">
                      {slide.number ? (
                        <Span className="widget-window-accordion__step">{stepLabel}</Span>
                      ) : null}
                      <H3 className="widget-window-accordion__title m-0 text-prose">
                        {slide.name}
                      </H3>
                    </div>
                    {isMobile ? (
                      <button
                        type="button"
                        className="faq-accordion-trigger ml-2 shrink-0 px-1 py-0.5"
                        aria-expanded={isMobileOpen}
                        aria-label={isMobileOpen ? 'Collapse' : 'Expand'}
                        onClick={() => {
                          setMobileOpenIndex((prev) => (prev === index ? -1 : index));
                        }}
                      >
                        <Span
                          className={clsx(
                            'text-lg font-medium text-prose-muted transition-transform',
                            isMobileOpen && 'rotate-45'
                          )}
                          aria-hidden
                        >
                          +
                        </Span>
                      </button>
                    ) : null}
                  </>
                )}
              </div>

              <div
                className={clsx(
                  'widget-window-accordion__body-shell',
                  isActive && 'widget-window-accordion__body-shell--open'
                )}
              >
                <div className="widget-window-accordion__body-inner">
                  {slide.subtitle ? (
                    <P className="widget-window-accordion__text m-0">{slide.subtitle}</P>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

WindowAccordionSlider.displayName = 'WindowAccordionSlider';
